import { create } from 'zustand';
import { Message, Conversation } from '../types/chat';

interface ChatStore {
    // UI State
    isOpen: boolean;
    activeConversation: string | null; // pubkey of active chat

    // Data
    conversations: Conversation[];
    messages: Map<string, Message[]>; // pubkey -> messages
    unreadCount: number;
    typing: Map<string, boolean>; // pubkey -> isTyping
    presence: Map<string, number>; // pubkey -> last active timestamp (unix)

    // Actions
    openChat: (pubkey?: string) => void;
    closeChat: () => void;
    setActiveConversation: (pubkey: string | null) => void;
    addMessage: (pubkey: string, message: Message) => void;
    setConversations: (conversations: Conversation[]) => void;
    markAsRead: (pubkey: string) => void;
    updateUnreadCount: (count: number) => void;
    setTyping: (pubkey: string, isTyping: boolean) => void;
    setPresence: (pubkey: string, timestamp: number) => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
    // Initial state
    isOpen: false,
    activeConversation: null,
    conversations: [],
    messages: new Map(),
    unreadCount: 0,
    typing: new Map(),
    presence: new Map(),

    // Actions
    openChat: (pubkey?: string) => {
        set({ isOpen: true });
        if (pubkey) {
            set({ activeConversation: pubkey });
        }
    },

    closeChat: () => {
        set({ isOpen: false, activeConversation: null });
    },

    setActiveConversation: (pubkey: string | null) => {
        set({ activeConversation: pubkey });
        if (pubkey) {
            // Mark as read when opening conversation
            get().markAsRead(pubkey);
        }
    },

    addMessage: (pubkey: string, message: Message) => {
        set(state => {
            const messages = new Map(state.messages);
            const existing = messages.get(pubkey) || [];
            messages.set(pubkey, [...existing, message]);
            return { messages };
        });
    },

    setConversations: (conversations: Conversation[]) => {
        set({ conversations });
    },

    markAsRead: (pubkey: string) => {
        set(state => {
            const conversations = state.conversations.map(c =>
                c.pubkey === pubkey ? { ...c, unread: 0 } : c
            );

            // Recalculate total unread
            const unreadCount = conversations.reduce((sum, c) => sum + c.unread, 0);

            return { conversations, unreadCount };
        });
    },

    updateUnreadCount: (count: number) => {
        set({ unreadCount: count });
    },

    setTyping: (pubkey, isTyping) => {
        set(state => {
            const next = new Map(state.typing);
            if (isTyping) next.set(pubkey, true);
            else next.delete(pubkey);
            return { typing: next };
        });
    },

    setPresence: (pubkey, timestamp) => {
        set(state => {
            const next = new Map(state.presence);
            // Only update if the new timestamp is newer
            const current = next.get(pubkey) || 0;
            if (timestamp > current) {
                next.set(pubkey, timestamp);
            }
            return { presence: next };
        });
    },
}));
